<template>
  <UIDropdownColumns
    v-if="!hideFieldsSettings"
    v-model="visibleFields"
    :data="fieldsOptions"
    :readonly
    placement="bottom-end"
    disallow-drag
  >
    <template #default="{ show }">
      <UIButton
        variant="gray"
        fill="outlined"
        :size="buttonSize"
        :leading="Cog6ToothIcon"
        :disabled="loading"
        @click="show"
      />
    </template>
  </UIDropdownColumns>
  <UIButton
    v-tooltip.top="'Zoom in'"
    :size="buttonSize"
    :leading="MagnifyingGlassPlusIcon"
    variant="gray"
    fill="outlined"
    :disabled="loading"
    @click="orgChartRef?.zoomIn"
  />
  <UIButton
    v-tooltip.top="'Zoom out'"
    :size="buttonSize"
    :leading="MagnifyingGlassMinusIcon"
    variant="gray"
    fill="outlined"
    :disabled="loading"
    @click="orgChartRef?.zoomOut"
  />
  <UIButton
    v-tooltip.top="'Reset zoom'"
    :size="buttonSize"
    :leading="ViewfinderCircleIcon"
    variant="gray"
    fill="outlined"
    :disabled="loading"
    @click="orgChartRef?.resetZoom"
  />
  <UIButton
    v-tooltip.top="collapseTooltip"
    :leading="collapseIcon"
    :size="buttonSize"
    variant="gray"
    fill="outlined"
    :disabled="loading"
    @click="handleToggleCollapse"
  />
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import type { Component } from 'vue'
import { OrgChart } from '@moonwane/vue3-org-chart'

import { ORGANIZATION_CHART_FIELDS } from './utils/const'

import { UIButton } from '@ui/buttons'
import { Cog6ToothIcon, MagnifyingGlassMinusIcon, MagnifyingGlassPlusIcon, ViewfinderCircleIcon } from '@heroicons/vue/24/outline'
import CollapseAllIcon from '@/assets/images/icons/collapse-all.svg'
import ExpandAllIcon from '@/assets/images/icons/expand-all.svg'
import { UIDropdownColumns } from '@ui/dropdowns'

type Props = {
  orgChartRef: typeof OrgChart.prototype.data
  readonly?: boolean
  loading?: boolean
  hideFieldsSettings?: boolean
  shrink?: boolean
}

const props = defineProps<Props>()

defineOptions({ inheritAttrs: false })

const hiddenFields = defineModel<string[]>('hiddenFields')

const isCollapsed = ref(true)

const innerHiddenFields = ref<string[] | undefined>(hiddenFields.value)

const fieldsOptions = computed(() => ORGANIZATION_CHART_FIELDS.map(field => ({
  label: `${field.charAt(0).toUpperCase()}${field.slice(1)}`,
  value: field,
})))

const fieldValues = computed(() => fieldsOptions.value.map(item => item.value))

const visibleFields = computed({
  get() {
    const excludedFields = innerHiddenFields.value || hiddenFields.value
    return fieldValues.value?.filter(field => !excludedFields?.includes(field))
  },
  set(value) {
    const result = fieldValues.value?.filter(field => !value.includes(field))
    innerHiddenFields.value = result
    hiddenFields.value = result
  },
})

const collapseTooltip = computed(() =>
  isCollapsed.value
    ? 'Expand all'
    : 'Collapse all',
)

const collapseIcon = computed(() => (isCollapsed.value ? ExpandAllIcon : CollapseAllIcon) as unknown as Component)

const buttonSize = computed(() => props.shrink ? 'xs' : 'sm')

const handleToggleCollapse = () => {
  if (isCollapsed.value) {
    props.orgChartRef?.expandAll()
  } else {
    props.orgChartRef?.collapseAll()
  }
  isCollapsed.value = !isCollapsed.value
}
</script>
